import { roleScopeLevelSchema } from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';

/**
 * A session's scope, or one role's. A `self` scope's id is the user's own id,
 * never a node's (docs/decisions/0087); a `self` role's is null.
 */
export const scopeSchema = z.object({
  level: roleScopeLevelSchema,
  id: z.uuid().nullable(),
});

export const currentUserSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.email(),
  twoFactorEnabled: z.boolean(),
  /** True when a role the user holds demands a second factor they have not set up. */
  mustEnrolTwoFactor: z.boolean(),
  /**
   * True when a role the user holds demands a second factor at all, so the
   * profile screen offers no way to turn it off — the server refuses one.
   */
  twoFactorRequiredByRole: z.boolean(),
  /**
   * Whether a telephone is held for this user (migration `0107`) — what the
   * entry form's *Bildirim İşlemleri* asks before offering the operator's own
   * row, since every notice there leaves on a telephone (docs/decisions/0080).
   * The number itself is not handed out.
   */
  hasPhone: z.boolean(),
  impersonation: z
    .object({
      adminId: z.uuid(),
      expiresAt: z.string(),
    })
    .nullable()
    .optional(),
  institution: z.object({
    id: z.uuid(),
    name: z.string(),
    code: z.string(),
    countryId: z.uuid(),
    zoneId: z.uuid(),
    regionId: z.uuid(),
  }),
  roles: z.array(z.object({ code: z.string(), name: z.string(), scope: scopeSchema })),
  permissions: z.array(z.string()),
  /**
   * What an in-force campaign staff assignment derives — the fulfilment work
   * of *Operasyon Sorumlusu* — kept apart from `permissions` on purpose
   * (docs/decisions/0090). A screen may offer its menu entry and its buttons
   * from it; the server honours it only on a donation the assignment reaches.
   * Empty when no assignment is in force.
   */
  staffPermissions: z.array(z.string()),
  scope: scopeSchema,
  /**
   * Whether this session has opened real names. Every session starts false and
   * the server decides it from the session row — docs/decisions/0136.
   */
  pseudonymsRevealed: z.boolean(),
  /** Whether the user holds `pseudonym.reveal`, so the profile menu offers *Rumuzları Çöz*. */
  canRevealPseudonyms: z.boolean(),
});

export type CurrentUser = z.infer<typeof currentUserSchema>;

/**
 * The password policy, one schema for every route that sets a password: at
 * least 12 characters and at most 200, and nothing else asked of it.
 */
export const newPasswordSchema = z.string().min(12, 'En az 12 karakter olmalıdır').max(200);

/**
 * A signed-in user's own account. Every route re-reads the user from the
 * session and never from the body, and each one that changes what a hijacked
 * session would want — the password, the address a reset link goes to, the
 * second factor — asks for the current password first.
 */
const profileErrors = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  INVALID_CREDENTIALS: commonErrors.INVALID_CREDENTIALS,
  ACCOUNT_LOCKED: commonErrors.ACCOUNT_LOCKED,
  TOO_MANY_REQUESTS: commonErrors.TOO_MANY_REQUESTS,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

/**
 * An address change waiting for its link: the address asked for and when the
 * link stops working. It is the caller's own input, so it is shown in full.
 */
export const pendingEmailChangeSchema = z.object({ email: z.email(), expiresAt: z.string() });

export type PendingEmailChange = z.infer<typeof pendingEmailChangeSchema>;

/**
 * Login either completes, or stops at the second factor. The client must handle
 * both, so the outcome is a discriminated union rather than an optional field.
 */
export const loginOutcomeSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('success'), user: currentUserSchema }),
  z.object({ status: z.literal('two_factor_required'), challengeToken: z.string() }),
]);

export const authContract = {
  impersonation: {
    start: oc
      .route({
        method: 'POST',
        path: '/auth/impersonation',
        summary: 'View a subordinate user as support',
      })
      .errors({
        UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
        PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
        TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
      })
      .input(z.object({ userId: z.uuid() }))
      .output(z.object({ user: currentUserSchema })),
    stop: oc
      .route({
        method: 'POST',
        path: '/auth/impersonation/stop',
        summary: 'Restore the original administrator session',
      })
      .errors({ UNAUTHENTICATED: commonErrors.UNAUTHENTICATED })
      .input(z.object({}))
      .output(z.object({ user: currentUserSchema })),
  },
  login: oc
    .route({ method: 'POST', path: '/auth/login', summary: 'Sign in with email and password' })
    .errors({
      INVALID_CREDENTIALS: commonErrors.INVALID_CREDENTIALS,
      ACCOUNT_LOCKED: commonErrors.ACCOUNT_LOCKED,
      TOO_MANY_REQUESTS: commonErrors.TOO_MANY_REQUESTS,
      VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
    })
    .input(
      z.object({
        email: z.email(),
        password: z.string().min(1),
      }),
    )
    .output(loginOutcomeSchema),

  verifyTwoFactor: oc
    .route({ method: 'POST', path: '/auth/2fa/verify', summary: 'Complete a two-factor challenge' })
    .errors({
      INVALID_CREDENTIALS: commonErrors.INVALID_CREDENTIALS,
      UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
      TOO_MANY_REQUESTS: commonErrors.TOO_MANY_REQUESTS,
    })
    .input(
      z.object({
        challengeToken: z.string().min(1),
        code: z.string().trim().min(6).max(16),
      }),
    )
    .output(z.object({ user: currentUserSchema })),

  logout: oc
    .route({ method: 'POST', path: '/auth/logout', summary: 'Sign out the current session' })
    .input(z.object({}))
    .output(z.object({ success: z.literal(true) })),

  me: oc
    .route({ method: 'GET', path: '/auth/me', summary: 'The signed-in user, roles and scope' })
    .errors({ UNAUTHENTICATED: commonErrors.UNAUTHENTICATED })
    .input(z.object({}))
    .output(z.object({ user: currentUserSchema })),

  requestPasswordReset: oc
    .route({
      method: 'POST',
      path: '/auth/password-reset/request',
      summary: 'Email a single-use reset link',
    })
    .errors({ TOO_MANY_REQUESTS: commonErrors.TOO_MANY_REQUESTS })
    .input(z.object({ email: z.email() }))
    // Always the same answer, so the endpoint cannot be used to discover which
    // addresses have accounts.
    .output(z.object({ accepted: z.literal(true) })),

  confirmPasswordReset: oc
    .route({ method: 'POST', path: '/auth/password-reset/confirm', summary: 'Set a new password' })
    .errors({
      UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
      VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
    })
    .input(
      z.object({
        token: z.string().min(1),
        password: newPasswordSchema,
      }),
    )
    .output(z.object({ success: z.literal(true) })),

  profile: {
    /** The signed-in user's own name, audited as `user` / `update`. */
    update: oc
      .route({ method: 'PATCH', path: '/auth/profile', summary: 'Change your own name' })
      .errors(profileErrors)
      .input(z.object({ name: z.string().trim().min(2).max(200) }))
      .output(z.object({ user: currentUserSchema })),

    /**
     * Asks to move the address sign-in and password reset use, after the current
     * password, and moves nothing: a single-use link goes to the new address and
     * a notice, naming it masked, to the current one. The answer is the same
     * whether or not another account holds the address — a taken address is sent
     * a notice instead of a link. Audited as `user` / `email_change_requested`;
     * 422 `EMAIL_UNCHANGED` for the current address.
     */
    changeEmail: oc
      .route({
        method: 'POST',
        path: '/auth/profile/email',
        summary: 'Ask to change your own email',
      })
      .errors(profileErrors)
      .input(z.object({ email: z.email().trim().max(320), currentPassword: z.string().min(1) }))
      .output(z.object({ pending: pendingEmailChangeSchema })),

    /** The address change waiting for its link, or none. */
    emailChange: oc
      .route({ method: 'GET', path: '/auth/profile/email', summary: 'Your pending email change' })
      .errors(profileErrors)
      .input(z.object({}))
      .output(z.object({ pending: pendingEmailChangeSchema.nullable() })),

    /**
     * Follows the link, as the signed-in user it was sent for, and moves the
     * address. Audited as `user` / `email_changed`. A link that is not the
     * caller's, was used or superseded, or has expired is 422
     * `EMAIL_CHANGE_LINK_INVALID`; an address another account took since is 409
     * `EMAIL_ADDRESS_IN_USE`, and the request stays open.
     */
    confirmEmailChange: oc
      .route({
        method: 'POST',
        path: '/auth/profile/email/confirm',
        summary: 'Confirm your new email',
      })
      .errors(profileErrors)
      .input(z.object({ token: z.string().min(1).max(200) }))
      .output(z.object({ user: currentUserSchema })),

    /**
     * Asks for the current password and applies the password policy. Every
     * session of the user is revoked — the caller's included — and the caller
     * is handed a new one, so a copy of the old cookie anywhere else stops
     * working. Audited as `user` / `password_changed`, carrying how many
     * sessions ended.
     */
    changePassword: oc
      .route({ method: 'POST', path: '/auth/password', summary: 'Change your own password' })
      .errors(profileErrors)
      .input(z.object({ currentPassword: z.string().min(1), newPassword: newPasswordSchema }))
      .output(z.object({ user: currentUserSchema, revokedSessions: z.number().int().min(0) })),
  },

  /**
   * Real names for the rest of this session, opened by a current TOTP code of
   * the session's owner and behind `pseudonym.reveal`. A recovery code is not
   * accepted, a code already used is refused, a wrong one counts toward the
   * durable lockout, and a support session never reveals. Docs/decisions/0136.
   */
  pseudonyms: {
    reveal: oc
      .route({
        method: 'POST',
        path: '/auth/pseudonyms/reveal',
        summary: 'Open real names for this session',
      })
      .errors({
        ...profileErrors,
        PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
      })
      .input(z.object({ code: z.string().trim().length(6) }))
      .output(z.object({ user: currentUserSchema })),
    hide: oc
      .route({
        method: 'POST',
        path: '/auth/pseudonyms/hide',
        summary: 'Mask names again for this session',
      })
      .errors({ UNAUTHENTICATED: commonErrors.UNAUTHENTICATED })
      .input(z.object({}))
      .output(z.object({ user: currentUserSchema })),
  },

  twoFactor: {
    start: oc
      .route({ method: 'POST', path: '/auth/2fa/start', summary: 'Begin TOTP enrolment' })
      .errors({ UNAUTHENTICATED: commonErrors.UNAUTHENTICATED, CONFLICT: commonErrors.CONFLICT })
      .input(z.object({}))
      .output(z.object({ secret: z.string(), otpauthUri: z.string() })),

    enable: oc
      .route({ method: 'POST', path: '/auth/2fa/enable', summary: 'Confirm and enable TOTP' })
      .errors({
        UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
        INVALID_CREDENTIALS: commonErrors.INVALID_CREDENTIALS,
        VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
      })
      .input(z.object({ secret: z.string().min(1), code: z.string().trim().length(6) }))
      // Shown once and never retrievable again.
      .output(z.object({ recoveryCodes: z.array(z.string()) })),

    /**
     * A user who has a second factor replaces it, from the profile. The first
     * step asks for the current password and either a current code or an
     * unused recovery code, and answers with a fresh secret, its `otpauth` URI
     * and a single-use `enrolmentToken` that binds the secret to this user. It
     * changes nothing the user has: the old secret and codes work until the
     * second step. A wrong password or code counts toward the sign-in
     * throttle and the durable lockout; a user with no second factor is 409
     * `CONFLICT`. Docs/decisions/0135.
     */
    reenrol: {
      start: oc
        .route({
          method: 'POST',
          path: '/auth/2fa/reenrol/start',
          summary: 'Begin replacing your second factor',
        })
        .errors(profileErrors)
        .input(
          z.object({
            currentPassword: z.string().min(1),
            code: z.string().trim().min(6).max(16),
          }),
        )
        .output(
          z.object({
            secret: z.string(),
            otpauthUri: z.string(),
            enrolmentToken: z.string(),
          }),
        ),

      /**
       * Proves the new secret with a code from it. Only then does it replace
       * the old secret, and the same transaction invalidates every old
       * recovery code, issues ten new ones — shown once — and ends every
       * session of the user; the caller is handed a new one. Audited as
       * `user` / `two_factor_reenrolled`.
       */
      confirm: oc
        .route({
          method: 'POST',
          path: '/auth/2fa/reenrol/confirm',
          summary: 'Confirm the new second factor',
        })
        .errors(profileErrors)
        .input(
          z.object({
            enrolmentToken: z.string().min(1),
            secret: z.string().min(1),
            code: z.string().trim().length(6),
          }),
        )
        .output(
          z.object({
            user: currentUserSchema,
            recoveryCodes: z.array(z.string()),
            revokedSessions: z.number().int().min(0),
          }),
        ),
    },

    disable: oc
      .route({ method: 'POST', path: '/auth/2fa/disable', summary: 'Turn TOTP off' })
      .errors(profileErrors)
      .input(z.object({ password: z.string().min(1) }))
      .output(z.object({ success: z.literal(true) })),
  },
};
